import { useEffect, useRef, useState, useCallback } from "react";
import { useI18n } from "../lib/i18n";

/**
 * iter130 — Interactive crop frame for the post-save photo prompt.
 *
 *  • Frame is fixed size; the image translates + scales inside it via
 *    `transform: translate(...) scale(...)`.
 *  • Drag = pan. Two-finger pinch = zoom, anchored on the pinch midpoint.
 *  • Zoom range: `minScale` (default 1 = fit to frame) → `maxScale`
 *    (default 3).
 *  • Image edges are clamped to the frame edges — the user can never see
 *    the dark canvas behind the photo.
 *  • Imperative `getCroppedBlob()` is exposed via the `cropRef` prop;
 *    callers grab the visible region as a PNG via the canvas API and
 *    upload it as a data URL.
 *  • A one-shot hint banner fades after 2 seconds.
 */
export default function InteractiveCrop({
  src,
  cropRef,
  frameHeight = 200,
  outputSize = { width: 1200, height: 800 },
  minScale = 1,
  maxScale = 3,
  testid = "interactive-crop",
}) {
  const { t } = useI18n();
  const containerRef = useRef(null);
  const imgRef = useRef(null);

  const [naturalSize, setNaturalSize] = useState(null);    // {w, h}
  const [containerSize, setContainerSize] = useState(null); // {w, h}
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [scale, setScale] = useState(1);
  const [showHint, setShowHint] = useState(true);

  // Hint fades after 2 seconds — single-shot per mount of a fresh photo.
  useEffect(() => {
    if (!src) return;
    setShowHint(true);
    const id = setTimeout(() => setShowHint(false), 2000);
    return () => clearTimeout(id);
  }, [src]);

  // Track container size (full-width × frameHeight) — needed for clamps.
  useEffect(() => {
    const update = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      setContainerSize({ w: rect.width, h: rect.height });
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  // Reset the transform whenever the source image changes.
  useEffect(() => {
    setOffset({ x: 0, y: 0 });
    setScale(1);
  }, [src]);

  /**
   * `baseFit` returns the cover-fit dimensions of the image inside the
   * container. The image always *covers* the frame so no dark gutter is
   * visible even at minScale=1.
   */
  const baseFit = useCallback(() => {
    if (!naturalSize || !containerSize) return null;
    const sx = containerSize.w / naturalSize.w;
    const sy = containerSize.h / naturalSize.h;
    const fit = Math.max(sx, sy);
    return {
      width: naturalSize.w * fit,
      height: naturalSize.h * fit,
    };
  }, [naturalSize, containerSize]);

  /** Clamp `(x, y, scale)` so the image edges never reveal the canvas. */
  const clamp = useCallback((x, y, s) => {
    const fit = baseFit();
    if (!fit || !containerSize) return { x, y, s };
    const w = fit.width * s;
    const h = fit.height * s;
    const maxX = (w - containerSize.w) / 2;
    const maxY = (h - containerSize.h) / 2;
    return {
      x: Math.max(-maxX, Math.min(maxX, x)),
      y: Math.max(-maxY, Math.min(maxY, y)),
      s: Math.max(minScale, Math.min(maxScale, s)),
    };
  }, [baseFit, containerSize, minScale, maxScale]);

  // ---------- Touch handlers ----------
  const touchState = useRef(null);

  const onTouchStart = (e) => {
    if (e.touches.length === 1) {
      touchState.current = {
        kind: "drag",
        startX: e.touches[0].clientX,
        startY: e.touches[0].clientY,
        baseX: offset.x,
        baseY: offset.y,
      };
    } else if (e.touches.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const dist = Math.hypot(dx, dy);
      touchState.current = {
        kind: "pinch",
        startDist: dist,
        baseScale: scale,
        baseX: offset.x,
        baseY: offset.y,
      };
    }
    if (showHint) setShowHint(false);
  };

  const onTouchMove = (e) => {
    if (!touchState.current) return;
    e.preventDefault();
    if (touchState.current.kind === "drag" && e.touches.length === 1) {
      const dx = e.touches[0].clientX - touchState.current.startX;
      const dy = e.touches[0].clientY - touchState.current.startY;
      const next = clamp(
        touchState.current.baseX + dx,
        touchState.current.baseY + dy,
        scale,
      );
      setOffset({ x: next.x, y: next.y });
    } else if (touchState.current.kind === "pinch" && e.touches.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const dist = Math.hypot(dx, dy);
      const ratio = dist / touchState.current.startDist;
      const nextScale = touchState.current.baseScale * ratio;
      const next = clamp(touchState.current.baseX, touchState.current.baseY, nextScale);
      setOffset({ x: next.x, y: next.y });
      setScale(next.s);
    }
  };

  const onTouchEnd = () => { touchState.current = null; };

  // ---------- Mouse fallback (desktop QA / pointer-only browsers) ----------
  const mouseState = useRef(null);
  const onMouseDown = (e) => {
    mouseState.current = {
      startX: e.clientX, startY: e.clientY,
      baseX: offset.x, baseY: offset.y,
    };
    if (showHint) setShowHint(false);
  };
  const onMouseMove = (e) => {
    if (!mouseState.current) return;
    const dx = e.clientX - mouseState.current.startX;
    const dy = e.clientY - mouseState.current.startY;
    const next = clamp(mouseState.current.baseX + dx, mouseState.current.baseY + dy, scale);
    setOffset({ x: next.x, y: next.y });
  };
  const onMouseUp = () => { mouseState.current = null; };
  const onWheel = (e) => {
    if (!naturalSize) return;
    e.preventDefault();
    const factor = e.deltaY < 0 ? 1.08 : 0.92;
    const next = clamp(offset.x, offset.y, scale * factor);
    setOffset({ x: next.x, y: next.y });
    setScale(next.s);
  };

  /**
   * Imperative API: capture the visible region as a PNG data URL.
   * Exposes via `cropRef.current = { getCroppedDataURL }` once mounted.
   */
  useEffect(() => {
    if (!cropRef) return;
    cropRef.current = {
      getCroppedDataURL: () => {
        if (!imgRef.current || !containerSize || !naturalSize) return null;
        const fit = baseFit();
        if (!fit) return null;

        // The image is rendered at `fit.width * scale × fit.height * scale`
        // and translated by `offset` from the container center. We capture
        // a `containerSize`-sized canvas of the visible region by mapping
        // container coords back to natural-image coords.
        const out = document.createElement("canvas");
        out.width = outputSize.width;
        out.height = outputSize.height;
        const ctx = out.getContext("2d");

        const renderedW = fit.width * scale;
        const renderedH = fit.height * scale;
        const ratioX = naturalSize.w / renderedW;
        const ratioY = naturalSize.h / renderedH;

        // Container center on screen
        const cx = containerSize.w / 2;
        const cy = containerSize.h / 2;
        // Image center is offset by (offset.x, offset.y) from container center
        const imgCenterX = cx + offset.x;
        const imgCenterY = cy + offset.y;
        // Top-left of the rendered image, in container coords
        const imgLeft = imgCenterX - renderedW / 2;
        const imgTop = imgCenterY - renderedH / 2;
        // Source rect in natural coords = (container 0,0 → containerSize.w,h)
        // translated to image space.
        const sx = (0 - imgLeft) * ratioX;
        const sy = (0 - imgTop) * ratioY;
        const sw = containerSize.w * ratioX;
        const sh = containerSize.h * ratioY;

        ctx.drawImage(imgRef.current, sx, sy, sw, sh, 0, 0, out.width, out.height);
        return out.toDataURL("image/jpeg", 0.9);
      },
    };
  }, [cropRef, baseFit, containerSize, naturalSize, offset, scale, outputSize]);

  const fit = baseFit();
  const renderedW = fit ? fit.width * scale : 0;
  const renderedH = fit ? fit.height * scale : 0;

  return (
    <div className="relative" data-testid={testid}>
      <div
        ref={containerRef}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchEnd}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
        onWheel={onWheel}
        className="relative select-none overflow-hidden touch-none"
        style={{
          width: "100%",
          height: frameHeight,
          borderRadius: 12,
          background: "#141414",
          cursor: "grab",
          touchAction: "none",
        }}
        data-testid={`${testid}-frame`}
      >
        {src && (
          <img
            ref={imgRef}
            src={src}
            alt=""
            crossOrigin="anonymous"
            onLoad={(e) => setNaturalSize({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })}
            draggable={false}
            data-testid={`${testid}-image`}
            style={{
              position: "absolute",
              left: "50%",
              top: "50%",
              width: renderedW || "auto",
              height: renderedH || "auto",
              transform: `translate(calc(-50% + ${offset.x}px), calc(-50% + ${offset.y}px))`,
              pointerEvents: "none",
              userSelect: "none",
              transformOrigin: "center center",
              transition: touchState.current || mouseState.current ? "none" : "transform 0.05s linear",
            }}
          />
        )}
      </div>
      <div
        aria-live="polite"
        data-testid={`${testid}-hint`}
        className="mt-2 text-center transition-opacity duration-700"
        style={{
          opacity: showHint && src ? 0.85 : 0,
          fontFamily: "'DM Sans', sans-serif",
          fontSize: 12,
          color: "#E8B84B",
          letterSpacing: "0.04em",
          pointerEvents: "none",
        }}
      >
        {t("sal_photo_crop_hint")}
      </div>
    </div>
  );
}
